import { bayer, type FrameSource, type PaletteKey, PixelCanvas, sphere, sprite } from '../../lib';
import {
  ASH,
  blendEdge,
  contactShadow,
  D,
  type EdgeLook,
  FANS,
  L2,
  leaf,
  type Mark,
  makeEdge,
  markIf,
  markOf,
  mixHash,
  paintGrass,
  pathInner,
  rand,
  range,
  scatterMarks,
  T,
  tnoise,
  tuft,
} from './shared';

const inTile = (x: number, y: number) => x >= 0 && y >= 0 && x < T && y < T;

// ---------------------------------------------------------------------------------------------------------
// Grass

/** One bigger tuft — full grass tiles only (a path's grass band stays plain). */
function grassFeature(c: PixelCanvas, seed: number): void {
  scatterMarks(c, rand(seed * 13 + 5), 1, FANS, (x, y) => inTile(x, y) && c.get(x, y) === 'g', 28, 3);
}

const grass = sprite({
  name: 'tile_grass',
  group: 'tile',
  size: [T, T],
  density: D,
  seed: 'tile_grass_a',
  // Hell's twin (ADR-0187): the lawn burnt to ash.
  recolor: { hell: ASH },
  notes: `Calm warm lawn: flat g with small tufts of 1-art-px blades (G blades, l tips; g/G/l only — no y/w on ground);
decals add meadow clumps, pebbles and soft lush patches. 8 variants picked by cell hash (the same lawn, never twice
alike).
4× density (ADR-0111): drawn natively at 64×64 (16×16 world), tufts are thin-bladed fans, not 4×4 blocks.
Rubric: S- R5 P5 L4 C4 A- F4. Weakest: the tufts are alike in size and spread evenly, so a big lawn reads a little
sprinkled; macro variation still relies on the decal scatter.
Contrast pass (2026-09): few, small tufts so gardeners, minions and pickups separate from the lawn in greyscale.`,
  anims: {
    variants: {
      frames: range(8).map(
        (v): FrameSource =>
          (c) => {
            const seed = 100 + v * 17;
            paintGrass(c, seed);
            grassFeature(c, seed);
          },
      ),
    },
  },
});

// ---------------------------------------------------------------------------------------------------------
// Grass decals: macro variation. 80×80 (20×20 world), origin centre, drawn on a share of grass cells; content stays
// within a 9.5-world-px radius so it never reaches more than 2 world px into a neighbour cell (a path's grass band is
// ≥ 2 px deep).

const DS = 20 * D;
const inDecal = (x: number, y: number) =>
  x >= 0 && y >= 0 && x < DS && y < DS && Math.hypot(x + 0.5 - DS / 2, y + 0.5 - DS / 2) < 9.5 * D;

// Grass vocabulary for the decals (lit from the top-left: `l` blade tips, `G` blades), rooted at their bottom-centre.
const FAN = tuft(6, 13, 501, 'l', 'G', 1.3);
const FAN2 = tuft(6, 12, 502, 'l', 'G', 1.2);
const WIDE = tuft(8, 14, 503, 'l', 'G', 1.5);
const VL = tuft(3, 9, 504, 'l', 'G', 1.2);
const SPRIG = tuft(2, 7, 505, 'l', 'G', 1);

/** Marks at their roots (art px), back to front. */
const put = (c: PixelCanvas, list: readonly (readonly [Mark, number, number])[]) => {
  for (const [m, x, y] of list) markIf(c, m, x, y, inDecal);
};

/** A weed rosette seen from above: broad leaves round a dark heart, lit edges top-left, a G contact shadow. */
function weed(c: PixelCanvas, cx: number, cy: number): void {
  const art = new PixelCanvas(DS, DS);
  const look = { body: 'G', lit: 'l', shade: 'D', rib: 'g' } as const;
  // Back leaves first (pointing up), the front ones (pointing down) over them.
  const leaves: [number, number, number][] = [
    [-2.2, 9, 3.6],
    [-0.9, 9.5, 3.6],
    [-3.3, 8.5, 3.4],
    [0.35, 9, 3.8],
    [2.6, 8.5, 3.6],
    [1.5, 10, 4],
  ];
  for (const [ang, len, wid] of leaves) leaf(art, cx, cy, ang, len, wid, look, inDecal);
  art
    .set(cx, cy, 'G')
    .set(cx - 1, cy, 'G')
    .set(cx, cy - 1, 'G');
  contactShadow(c, art, 'G', inDecal);
}

/** A warm round pebble half sunk in the lawn: lit top-left (e glint, t), m body, n under; a G contact shadow. */
function pebble(c: PixelCanvas, cx: number, cy: number): void {
  const art = new PixelCanvas(DS, DS);
  sphere(art, cx, cy, 7, 5.5, ['n', 'm', 't', 'e'], { bands: [0.1, 0.55, 0.93] });
  contactShadow(c, art, 'G', inDecal);
}

/**
 * A soft patch of deeper, lusher lawn: a noise-wobbled `G` ellipse whose rim is blades (dark tips standing up out of it
 * into the lawn, a sparse dither only along its lower edge), lighter `g` tufts inside, lit tufts on its upper lip.
 */
function lushPatch(c: PixelCanvas): void {
  const seed = 14;
  const [cx, cy, rx, ry] = [10 * D, 10.5 * D, 8.5 * D, 5.5 * D];
  const dd = (x: number, y: number) =>
    Math.hypot((x + 0.5 - cx) / rx, (y + 0.5 - cy) / ry) + (tnoise(x, y, 4 * D, seed, seed, 1) - 0.5) * 0.45;
  for (let y = 0; y < DS; y++) {
    for (let x = 0; x < DS; x++) {
      if (!inDecal(x, y)) continue;
      const v = dd(x, y);
      const lower = y + 0.5 > cy;
      if (v < 0.8 || (lower && v < 0.86 && bayer(x, y) < 0.25)) c.set(x, y, 'G');
    }
  }
  // Blades of the patch standing up over its rim (upper half), 1-4 px.
  for (let x = 2; x < DS - 2; x++) {
    for (let y = 2; y < DS - 2; y++) {
      if (c.get(x, y) !== 'G' || c.get(x, y - 1) === 'G' || y + 0.5 > cy + ry * 0.2) continue;
      const h = Math.floor(mixHash(x, y, 141) * 5);
      if (h === 0 || mixHash(x, y, 142) < 0.35) continue;
      const lean = mixHash(x, y, 143) < 0.5 ? 0 : x < cx ? -1 : 1;
      for (let k = 1; k <= h; k++) {
        const bx = x + (k > 2 ? lean : 0);
        if (inDecal(bx, y - k)) c.set(bx, y - k, 'G');
      }
    }
  }
  const r = rand(seed);
  const inside = (x: number, y: number) => c.get(x, y) === 'G' && c.get(x, y + 2) === 'G' && c.get(x - 2, y) === 'G';
  for (let i = 0, n = 0; i < 40 && n < 4; i++) {
    const x = Math.round(cx + (r.next() - 0.5) * rx * 1.2);
    const y = Math.round(cy + (r.next() - 0.3) * ry);
    if (markIf(c, tuft(3, 6, 520 + i, 'l', 'g', 1.2), x, y, inside)) n++;
  }
  put(c, [
    [VL, Math.round(cx - rx * 0.5), Math.round(cy - ry * 0.75)],
    [tuft(4, 10, 506, 'l', 'G', 1.3), Math.round(cx + rx * 0.25), Math.round(cy - ry * 0.85)],
  ]);
}

const decal = sprite({
  name: 'decal_grass',
  group: 'tile',
  size: [DS, DS],
  density: D,
  origin: [DS / 2, DS / 2],
  seed: 'decal_grass_a',
  notes: `Lawn decor scattered over grass cells: a meadow clump of tall tufts, a leafy weed rosette, a warm pebble half sunk
in the lawn, a soft lush patch (blades standing up out of its upper rim, one sparse dither row on its lower one).
4× density (ADR-0111): blades and leaf edges 1 art px, the pebble a lit volume, all at their 1× places.
Rubric: S4 R5 P5 L4 C4 A- F4. Weakest: the big tufts' roots merge into a small solid G foot.
Storybook (A) direction, picked in the 2026-09 art review. Contrast pass (2026-09): meadow clump of three tufts.`,
  anims: {
    variants: {
      frames: [
        // a meadow clump: overlapping tufts, back to front (kept small so the lawn stays calm around units)
        (c) =>
          put(c, [
            [FAN2, 34, 39],
            [WIDE, 48, 47],
            [VL, 38, 63],
          ]),
        // leafy weed with a couple of tufts
        (c) => {
          put(c, [
            [SPRIG, 46, 35],
            [FAN2, 26, 55],
          ]);
          weed(c, 40, 42);
          put(c, [[VL, 57, 57]]);
        },
        // a warm pebble nested in grass
        (c) => {
          put(c, [
            [FAN, 30, 43],
            [VL, 58, 43],
          ]);
          pebble(c, 45, 48);
          put(c, [[SPRIG, 32, 60]]);
        },
        lushPatch,
      ],
    },
  },
});

// ---------------------------------------------------------------------------------------------------------
// Paths use a 16-frame autotile: frame = N|E|S|W bits of PATH neighbours (dirt + cobble). Open sides blend into
// the lawn through the shared rounded grass edge; closed sides run to the tile edge.

/** Pebbles lit from the top-left (t cap, n shadow side), and grit; on edge tiles only. */
const PEBBLES = [
  ['.tt.', 'ttmn', 'tmmn', '.nn.'],
  ['..tt..', '.ttmmn', 'tmmmmn', '.nnnn.'],
  ['.te.', 'ttmn', '.nn.'],
].map(markOf);
const GRIT = [['tn'], ['t.', '.n'], ['t', 'n']].map(markOf);
const DIRT_LOOK: EdgeLook = { shade: ['N', 'n', 'n', 'n'], lit: ['n'] };

export function dirt(mask: number): FrameSource {
  return (c) => {
    const seed = 300 + mask * 11;
    const edge = makeEdge(mask);
    // Flat warm earth: the lane is the backdrop the fights happen on, so it stays calm (no tone speckle).
    c.rect(0, 0, T, T, 'm');
    const r = rand(seed + 97);
    // Interiors, straight runs and plaza sides (three path neighbours) repeat every 16 px across a field, and a pebble
    // there lined up into a dotted grid: they stay bare (`decal_dirt` scatters stones by cell). Corners and ends keep a
    // couple.
    const sides = range(4).filter((i) => mask & (1 << i)).length;
    if (mask !== 5 && mask !== 10 && sides < 3) {
      const ok = (x: number, y: number) => inTile(x, y) && edge.f(x, y) > 1.5;
      const placed = scatterMarks(c, r, 2, PEBBLES, ok, 22, 3);
      scatterMarks(c, r, 2, GRIT, ok, 14, 3, placed);
    }
    blendEdge(c, edge, DIRT_LOOK, 500 + mask);
  };
}

/** The lane's inner corners: the lawn closing each concave corner (`inner` anim: 0 NE, 1 SE, 2 SW, 3 NW). */
export const dirtInner = (corner: number) => pathInner((c) => c.rect(0, 0, T, T, 'm'), DIRT_LOOK, corner);

const path = sprite({
  name: 'tile_dirt',
  group: 'tile',
  size: [T, T],
  density: D,
  seed: 'tile_dirt_a',
  // Hell's twin (ADR-0187): the lane through ash.
  recolor: { hell: ASH },
  notes: `Dirt lane autotile. Warm calm lane; soft rounded grass edges (a lit l lip and blades standing over the south
banks, a shaded G lip with an N/n earth face under the north banks, blade tips drooping over it); pebbles only on
corners and ends (straight runs, plaza sides and interiors repeat every 16 px: decal_dirt scatters stones by cell).
inner (0 NE, 1 SE, 2 SW, 3 NW): the lawn closing a concave corner (the two neighbours' bands meeting, rounded, with
their lips, bands and blades), drawn over the cell by the client.
4× density (ADR-0111): the 1× edge field sampled at art-px centres (smooth curves), 1-art-px lips and blades.
Rubric: S- R5 P5 L5 C4 A- F4. Weakest: an inner corner is tight (the neighbours' bands fix its size); wide lanes are
plain in the middle. Contrast pass (2026-09): flat m earth so units and pickups read on the lane in greyscale.`,
  anims: { mask: { frames: range(16).map(dirt) }, inner: { frames: range(4).map(dirtInner) } },
});

const STONES = 10;
const STONE_SEED = 8801;
/**
 * Stone layout: Voronoi sites on the tile torus (identical in every mask), so stones continue across seams. Poisson-ish
 * with a falling spacing, so stones vary in size and the 16-px repeat doesn't line up into a diamond lattice.
 */
const STONE_SITES = (() => {
  const pts: (readonly [number, number])[] = [];
  const td = (a: number, b: number) => Math.min(Math.abs(a - b), T - Math.abs(a - b));
  for (let k = 0; pts.length < STONES && k < 20000; k++) {
    const x = mixHash(k, 1, STONE_SEED) * T;
    const y = mixHash(k, 2, STONE_SEED) * T;
    const min = 19 - k / 1500;
    if (pts.every(([px, py]) => Math.hypot(td(px, x), td(py, y)) >= min)) pts.push([x, y]);
  }
  return pts;
})();
/** Joint half-width between stones, art px. */
const STONE_GAP = 1.1;
/** Rounds the stone corners (0 = pure Voronoi, 1 = pebble-like). */
const STONE_ROUND = 0.35;

const wrap = (v: number) => ((v % T) + T) % T;

type Site = readonly [number, number];

/**
 * Distances to the nearest and second-nearest stone site (torus), the unit normal towards the joint between them, and
 * the nearest site's index.
 */
function nearestSites(sites: readonly Site[], x: number, y: number): [number, number, number, number, number] {
  let d1 = 1e9;
  let d2 = 1e9;
  let i1 = 0;
  let a: Site = [0, 0];
  let b: Site = [0, 0];
  sites.forEach(([sx, sy], i) => {
    let dx = sx - (x + 0.5);
    let dy = sy - (y + 0.5);
    dx -= Math.round(dx / T) * T;
    dy -= Math.round(dy / T) * T;
    const d = Math.hypot(dx, dy);
    if (d < d1) {
      [d2, b] = [d1, a];
      [d1, a, i1] = [d, [dx, dy], i];
    } else if (d < d2) [d2, b] = [d, [dx, dy]];
  });
  const nx = b[0] - a[0];
  const ny = b[1] - a[1];
  const l = Math.hypot(nx, ny) || 1;
  return [d1, d2, nx / l, ny / l, i1];
}

/** A stone's ramp: lit rim, body, shaded lip, and how wide its lit rim is (art px). */
type StoneTone = readonly [lit: PaletteKey, body: PaletteKey, shade: PaletteKey, litW: number];
const STONE: StoneTone = ['4', '3', '2', 1];
/** A rounder, sun-worn stone: a wider lit rim (only on fill variants, inside them). */
const PALE: StoneTone = ['4', '3', '2', 2.6];

/**
 * Stone field: a slate joint where the two nearest sites are nearly equidistant (Voronoi border) plus rounded cell
 * corners; each stone is lit on its top/left rim (1 px) and shadowed along its bottom/right rim (2 px, its 3/4 front
 * lip), the joint a 1 deep line in a 2 groove. `tone` picks a stone's ramp by its site.
 */
function stones(c: PixelCanvas, sites: readonly Site[] = STONE_SITES, tone: (i: number) => StoneTone = () => STONE) {
  const cell = range(T * T).map((i) => {
    const [d1, d2, nx, ny, s] = nearestSites(sites, i % T, Math.floor(i / T));
    const border = (d2 - d1) / 2;
    const far = d1 / Math.max(1, (d1 + d2) / 2);
    const round = far > 1 - STONE_ROUND * 0.25 ? STONE_ROUND * 1.2 * D : 0;
    // Distance to the stone's (rounded) rim, art px; ≤ 0 inside the joint.
    return { rim: border - STONE_GAP - round, lit: nx * -0.55 + ny * -0.83, s };
  });
  const at = (x: number, y: number) => cell[wrap(y) * T + wrap(x)] as (typeof cell)[number];
  for (let y = 0; y < T; y++) {
    for (let x = 0; x < T; x++) {
      const { rim, lit, s } = at(x, y);
      const [l, b, d, lw] = tone(s);
      if (rim <= -1.8) c.set(x, y, '1');
      else if (rim <= 0) c.set(x, y, '2');
      else if (rim <= lw && lit > 0.2 - (lw - 1) * 0.15) c.set(x, y, l);
      else if (rim <= 2 && lit < -0.25) c.set(x, y, d);
      else c.set(x, y, b);
    }
  }
}

const COBBLE_LOOK: EdgeLook = { shade: ['1', '1', '1', null], lit: [null] };

function cobble(mask: number): FrameSource {
  return (c) => {
    stones(c);
    blendEdge(c, makeEdge(mask), COBBLE_LOOK, 800 + mask);
  };
}

/**
 * Where a fill variant may differ from the base layout: clear of the border (so it meets every edge frame) and of the
 * four corners (an inner corner's wedge is drawn there over whichever variant lies under it).
 */
const FILL_FREE = (x: number, y: number) => {
  const m = 5;
  const k = 16;
  const nearX = x < k || x >= T - k;
  const nearY = y < k || y >= T - k;
  return x >= m && y >= m && x < T - m && y < T - m && !(nearX && nearY);
};

/**
 * Plaza fill variants: the stones away from the border and corners laid out again (Poisson sites re-drawn in the middle,
 * kept only if every changed pixel is `FILL_FREE`), a pale stone or two, a hairline crack, a moss tuft in a joint.
 * Variant 0 is the base tile.
 */
const COBBLE_FILLS: readonly ((c: PixelCanvas) => void)[] = (() => {
  const base = new PixelCanvas(T, T);
  stones(base);
  const out: ((c: PixelCanvas) => void)[] = [(c) => stones(c)];
  for (let seed = 1; out.length < 8 && seed < 800; seed++) {
    const r = rand(9300 + seed);
    // Keep the sites whose stones touch the border band or a corner; re-draw the others inside.
    const keep = STONE_SITES.filter(([x, y]) => !(x > 18 && x < T - 18 && y > 18 && y < T - 18));
    const sites: Site[] = [...keep];
    for (let n = 0, tries = 0; n < 3 && tries < 200; tries++) {
      const p: Site = [16 + r.next() * (T - 32), 16 + r.next() * (T - 32)];
      if (sites.every(([x, y]) => Math.hypot(x - p[0], y - p[1]) > 13)) {
        sites.push(p);
        n++;
      }
    }
    const pale = new Set(range(sites.length).filter((i) => i >= keep.length && r.next() < 0.35));
    const art = new PixelCanvas(T, T);
    stones(art, sites, (i) => (pale.has(i) ? PALE : STONE));
    let ok = true;
    for (let y = 0; y < T && ok; y++)
      for (let x = 0; x < T && ok; x++) if (art.get(x, y) !== base.get(x, y) && !FILL_FREE(x, y)) ok = false;
    if (!ok) continue;
    // A hairline crack across one stone, a moss tuft in a joint (both inside the free area).
    const crack = r.next() < 0.6 ? ([26 + r.int(0, 10), 28 + r.int(0, 8)] as const) : null;
    const moss = r.next() < 0.6;
    out.push((c) => {
      c.blit(art);
      if (crack) {
        for (let t = 0, [x, y] = crack; t < 8; t++) {
          const p = c.get(x, y);
          if ((p === '3' || p === '4') && FILL_FREE(x, y)) c.set(x, y, '2');
          x += 1;
          y += t % 3 === 1 ? 1 : 0;
        }
      }
      // Moss grown along one joint: a run of its deep-joint pixels from near the middle, G with the odd lit g.
      if (moss) {
        const start = range(T * T)
          .map((i) => [i % T, Math.floor(i / T)] as const)
          .filter(([x, y]) => c.get(x, y) === '1' && FILL_FREE(x, y) && Math.hypot(x - 32, y - 32) < 12)[0];
        const run: (readonly [number, number])[] = start ? [start] : [];
        for (let k = 0; k < run.length && run.length < 14; k++) {
          const [x, y] = run[k] as readonly [number, number];
          for (const [dx, dy] of [
            [1, 0],
            [0, 1],
            [-1, 0],
            [0, -1],
            [1, 1],
            [-1, 1],
          ] as const) {
            const p = [x + dx, y + dy] as const;
            if (c.get(p[0], p[1]) === '1' && FILL_FREE(p[0], p[1]) && !run.some(([a, b]) => a === p[0] && b === p[1]))
              run.push(p);
          }
        }
        for (const [k, [x, y]] of run.entries()) c.set(x, y, k % 4 === 1 ? 'g' : 'G');
      }
    });
  }
  return out;
})();

/** The plaza's inner corners (stones of the base layout under the wedge: every fill variant keeps them there). */
const cobbleInner = (corner: number) => pathInner((c) => stones(c), COBBLE_LOOK, corner);

const plaza = sprite({
  name: 'tile_cobble',
  group: 'tile',
  size: [T, T],
  density: D,
  seed: 'tile_cobble_a',
  // Hell's twin (ADR-0187): the plaza in ash.
  recolor: { hell: ASH },
  notes: `Cobblestone plaza autotile (same mask bits as tile_dirt). Irregular rounded cobbles (slate 2/3/4) with lit
top-left rims, shadowed front lips and soft slate joints; rounded grass edges.
4× density (ADR-0111): ten Poisson sites per tile (the 1× eight sat on a near lattice that read as a diamond grid
at 4×), rims and joints 1-2 art px. fill: 8 full-plaza variants (the middle stones laid out again, a worn stone, a
hairline crack, moss in a joint), changed only away from the border and the corners, so they meet every edge frame and
every inner wedge. inner: the lawn closing a concave corner, like tile_dirt's.
Rubric: S- R4 P5 L4 C4 A- F4. Weakest: the border ring of stones is the same in every variant; cobble meets dirt in a
straight cut (both are path to the mask). Storybook (A) direction, picked in the 2026-09 art review.`,
  anims: {
    mask: { frames: range(16).map(cobble) },
    inner: { frames: range(4).map(cobbleInner) },
    fill: { frames: COBBLE_FILLS },
  },
});

// ---------------------------------------------------------------------------------------------------------
// Lane decals: what the client scatters on a share of dirt cells (the tiles stay bare where they repeat). 80×80
// (20×20 world), origin centre; content kept within ~5 world px of it, so on a lane's edge cell it stays on the earth.

const inLane = (x: number, y: number) => Math.hypot(x + 0.5 - DS / 2, y + 0.5 - DS / 2) < 5 * D;

/** A pebble lit from the top-left on the lane: t cap, m body, n shaded side, an N contact. */
function lanePebble(c: PixelCanvas, cx: number, cy: number, rx: number, ry: number): void {
  const art = new PixelCanvas(DS, DS);
  sphere(art, cx, cy, rx, ry, ['n', 'm', 't', 't'], { bands: [0.15, 0.55, 0.95] });
  contactShadow(c, art, 'N', inLane);
}

/** A hairline crack in dry earth: 1-px n lines branching off a main one, an N gape at its widest, a lit t lip. */
function crack(c: PixelCanvas, seed: number): void {
  const r = rand(seed);
  const walk = (x: number, y: number, ang: number, len: number, depth: number) => {
    for (let t = 0; t < len; t++) {
      if (!inLane(Math.round(x), Math.round(y))) return;
      c.set(x, y, depth === 0 && t > len * 0.3 && t < len * 0.6 ? 'N' : 'n');
      if (depth === 0 && t > len * 0.3 && t < len * 0.6) c.set(x, y - 1, 't');
      if (depth < 1 && t > 2 && r.next() < 0.12)
        walk(x, y, ang + (r.next() < 0.5 ? -1 : 1) * (0.6 + r.next() * 0.5), len * 0.4, depth + 1);
      ang += (r.next() - 0.5) * 0.6;
      x += Math.cos(ang);
      y += Math.sin(ang);
    }
  };
  walk(DS / 2 - 12, DS / 2 + 2, -0.2, 26, 0);
}

const decalDirt = sprite({
  name: 'decal_dirt',
  group: 'tile',
  size: [DS, DS],
  density: D,
  origin: [DS / 2, DS / 2],
  notes: `Lane decor the client scatters on ~25% of dirt cells (ADR-0111): the regular pebbles left the repeating lane tiles
(straight runs, plaza sides, interiors: a pebble per tile lined up into a dotted grid). 0 a pebble pair, 1 a flat stone,
2 a hairline crack, 3 a dry tuft by a pebble, 4 a twig, 5 grit. Earth tones (t/m/n), small and low: the lane stays calm
and the minions pop. 80×80, origin centre, content within 5 world px of it. 4× density (ADR-0111).
Rubric: S4 R4 P5 L4 C4 A- F4. Weakest: the crack is the only feature taller than a few px; six variants repeat on a long lane.`,
  anims: {
    variants: {
      frames: [
        (c) => {
          lanePebble(c, 36, 38, 4.5, 3.5);
          lanePebble(c, 46, 44, 3, 2.4);
          markIf(c, markOf(['tn']), 30, 46, inLane);
        },
        (c) => {
          lanePebble(c, 40, 40, 6, 4.2);
          markIf(c, markOf(['t.', '.n']), 49, 43, inLane);
        },
        (c) => crack(c, 51),
        (c) => {
          markIf(c, tuft(4, 7, 530, 't', 'n', 1.3), 38, 44, inLane);
          lanePebble(c, 47, 44, 2.5, 2);
        },
        (c) => {
          // A dry twig: an n stick lit t on its top, a little fork.
          for (let t = 0; t <= 16; t++) {
            const x = 32 + t;
            const y = Math.round(44 - t * 0.35);
            c.set(x, y, 't').set(x, y + 1, 'n');
            if (c.get(x + 1, y + 2) === null) c.set(x + 1, y + 2, 'N');
          }
          c.set(42, 40, 'n').set(43, 39, 'n').set(44, 38, 't');
        },
        (c) => {
          for (const [x, y] of [
            [33, 37],
            [41, 35],
            [45, 42],
            [36, 45],
            [48, 37],
          ] as const)
            markIf(c, markOf(['t.', 'mn']), x, y, inLane);
        },
      ],
    },
  },
});

// ---------------------------------------------------------------------------------------------------------
// Flowers

const FS = 7 * D;

/** A petal: a rotated ellipse (centre, radii, angle). */
type Petal = readonly [cx: number, cy: number, rx: number, ry: number, ang?: number];
/** A bloom's ramp: rim (its own dark tone, the 1× sel-out), shade, body, light. */
type BloomLook = readonly [rim: PaletteKey, shade: PaletteKey, body: PaletteKey, light: PaletteKey];

/**
 * Petals drawn back to front, each a lit volume (light top-left): light / body / shade by its normal, a 1-px seam of
 * shade where it overlaps an earlier petal; then the bloom's rim in its own dark tone, 1 px round it and 2 px on its
 * lower-right (the 1× sel-out at half a world px).
 */
function bloom(c: PixelCanvas, petals: readonly Petal[], [rim, shade, body, light]: BloomLook, seams = true): void {
  const owner = new Int16Array(FS * FS).fill(-1);
  const col: (PaletteKey | null)[] = new Array(FS * FS).fill(null);
  const ll = Math.hypot(L2[0], L2[1], 0.9);
  petals.forEach(([cx, cy, rx, ry, ang = 0], i) => {
    const [ca, sa] = [Math.cos(ang), Math.sin(ang)];
    for (let y = 0; y < FS; y++) {
      for (let x = 0; x < FS; x++) {
        const dx = x + 0.5 - cx;
        const dy = y + 0.5 - cy;
        const u = (dx * ca + dy * sa) / rx;
        const v = (-dx * sa + dy * ca) / ry;
        const d = u * u + v * v;
        if (d > 1) continue;
        // Normal of the petal's dome, back in canvas axes.
        const [nx, ny] = [u * ca - v * sa, u * sa + v * ca];
        const dot = (nx * L2[0] + ny * L2[1] + Math.sqrt(1 - d) * 0.9) / ll;
        const prev = owner[y * FS + x] as number;
        owner[y * FS + x] = i;
        col[y * FS + x] = dot > 0.78 ? light : dot > 0.3 ? body : shade;
        if (seams && prev >= 0 && d > 0.6 && nx * L2[0] + ny * L2[1] > -0.2) col[y * FS + x] = shade;
      }
    }
  });
  const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < FS && y < FS && (owner[y * FS + x] as number) >= 0;
  for (let y = 0; y < FS; y++) {
    for (let x = 0; x < FS; x++) {
      if (!inside(x, y)) continue;
      const edge =
        !inside(x - 1, y) ||
        !inside(x + 1, y) ||
        !inside(x, y - 1) ||
        !inside(x, y + 1) ||
        !inside(x + 2, y + 1) ||
        !inside(x + 1, y + 2) ||
        !inside(x, y + 2);
      c.set(x, y, edge ? rim : (col[y * FS + x] as PaletteKey));
    }
  }
}

/** A 2-px G stem from the ground (13, 27) up to `top`, bending by `bend`. */
function stem(c: PixelCanvas, top: number, bend = 0): void {
  for (let y = 27; y >= top; y--) {
    const u = (27 - y) / Math.max(1, 27 - top);
    const x = 13 + Math.round(bend * u * u);
    c.set(x, y, 'G').set(x + 1, y, 'G');
  }
}

/** Light leaves with a shaded lower edge: they read on the lawn (a g body would vanish into it). */
const LEAF_LOOK = { body: 'l', lit: 'l', shade: 'G' } as const;

/** Paints a dot cluster: [x, y, colour] (seed heads, eyes, stamens). */
const dots = (c: PixelCanvas, list: readonly (readonly [number, number, PaletteKey])[]) => {
  for (const [x, y, k] of list) c.set(x, y, k);
};

const FLOWERS: readonly ((c: PixelCanvas) => void)[] = [
  // red poppy: a round cup of four petals, dark seed head
  (c) => {
    stem(c, 12, -1);
    leaf(c, 13, 22, Math.PI * 0.92, 7, 2.2, LEAF_LOOK);
    leaf(c, 14, 19, -Math.PI * 0.08, 7, 2.2, LEAF_LOOK);
    bloom(
      c,
      [
        [9.5, 7, 5.5, 5],
        [18.5, 7, 5.5, 5],
        [14, 4.5, 5, 4],
        [14, 10.5, 6.5, 4.5],
      ],
      ['R', 'R', 'r', 'i'],
    );
    c.ellipse(14, 7.5, 2.2, 1.9, 'N');
    dots(c, [
      [13, 6, 'R'],
      [12, 7, 'P'],
    ]);
  },
  // yellow buttercup: a shallow cup of three glossy petals
  (c) => {
    stem(c, 12, 1);
    leaf(c, 14, 21, -Math.PI * 0.1, 7, 2.2, LEAF_LOOK);
    leaf(c, 13, 24, Math.PI * 0.95, 6, 2, LEAF_LOOK);
    // Three petal tips over the cup's bowl (one smooth shape: no seams), its shadowed throat a U arc.
    bloom(
      c,
      [
        [9.5, 6.5, 3.4, 4.2, -0.3],
        [18.5, 6.5, 3.4, 4.2, 0.3],
        [14, 5.5, 3.6, 4.6],
        [14, 9.5, 7, 4],
      ],
      ['U', 'U', 'Y', 'y'],
      false,
    );
    c.ellipse(14, 8.5, 3.2, 1.6, 'U');
    dots(c, [
      [13, 8, 'Y'],
      [15, 8, 'Y'],
      [14, 9, 'o'],
    ]);
  },
  // white daisy: eight petals round a gold eye
  (c) => {
    stem(c, 13, 0);
    leaf(c, 13, 21, Math.PI * 0.85, 7, 2.2, LEAF_LOOK);
    bloom(
      c,
      range(8).map((i): Petal => {
        const a = (i / 8) * Math.PI * 2 + 0.2;
        return [14 + Math.cos(a) * 5.5, 8 + Math.sin(a) * 4.3, 4, 2.1, a];
      }),
      ['4', '5', 'e', 'e'],
    );
    c.ellipse(14, 8, 2.6, 2.2, 'Y');
    dots(c, [
      [15, 9, 'U'],
      [14, 9, 'U'],
      [15, 8, 'U'],
      [13, 7, 'y'],
    ]);
  },
  // purple pansy: two big upper petals, three lower, a gold eye
  (c) => {
    stem(c, 13, 0);
    leaf(c, 14, 20, -Math.PI * 0.15, 7, 2.3, LEAF_LOOK);
    leaf(c, 13, 23, Math.PI * 0.9, 6, 2, LEAF_LOOK);
    bloom(
      c,
      [
        [10, 5.5, 4.5, 4.5],
        [18, 5.5, 4.5, 4.5],
        [8.5, 10.5, 4, 3.5],
        [19.5, 10.5, 4, 3.5],
        [14, 11.5, 4.5, 3.8],
      ],
      ['P', 'P', 'p', 'i'],
    );
    c.ellipse(14, 9, 1.6, 1.4, 'Y');
    dots(c, [
      [14, 11, 'P'],
      [12, 10, 'P'],
      [16, 10, 'P'],
    ]);
  },
  // weed: a low leafy rosette (dark heart), the 1× weed's G leaves with lit l edges
  (c) => {
    const look = { body: 'G', lit: 'l', shade: 'D', rib: 'g' } as const;
    for (const [ang, len, wid] of [
      [-2.5, 9, 2.8],
      [-0.6, 9, 2.8],
      [-1.55, 10, 2.8],
      [-3.0, 8, 2.6],
      [-0.1, 8, 2.6],
    ] as const)
      leaf(c, 13.5, 23, ang, len, wid, look);
    c.ellipse(13.5, 23.5, 1.6, 1.3, 'D');
  },
  // blue forget-me-nots: a spray of three small five-petal flowers, gold eyes
  (c) => {
    stem(c, 9, -1);
    c.line(14, 21, 20, 16, 'G').line(13, 20, 8, 16, 'G');
    leaf(c, 14, 23, -Math.PI * 0.1, 6, 2, LEAF_LOOK);
    const head = (x: number, y: number, r: number) => {
      const petals = range(5).map((i): Petal => {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        return [x + Math.cos(a) * r * 0.9, y + Math.sin(a) * r * 0.9, r * 0.85, r * 0.85];
      });
      bloom(c, [...petals, [x, y, r * 0.8, r * 0.8]], ['B', 'B', 'b', 'c'], false);
      c.set(Math.floor(x), Math.floor(y), 'y');
    };
    head(7.5, 13.5, 3);
    head(20, 14, 3);
    head(13.5, 5.5, 3.3);
  },
];

const flower = sprite({
  name: 'flower',
  group: 'tile',
  size: [FS, FS],
  density: D,
  origin: [3 * D, 6 * D],
  // Hell's twin (ADR-0187): every flower a flame on a charred stalk.
  recolor: {
    hell: {
      G: 'k',
      g: '1',
      l: '2',
      D: 'k',
      B: 'u',
      b: 'o',
      c: 'Y',
      P: 'R',
      p: 'u',
      i: 'o',
      R: 'N',
      r: 'u',
      e: 'Y',
      Y: 'o',
      y: 'Y',
      U: 'u',
      4: '2',
      5: '3',
      N: 'k',
      o: 'y',
    },
  },
  notes: `Ground decoration, each variant its own species so a bed reads as a natural mix: 0 red poppy, 1 yellow buttercup,
2 white daisy, 3 purple pansy, 4 weed (leafy rosette), 5 blue forget-me-nots (team-side accents). Soft round
blooms (lit petal domes, 1-px seams) with a rim in the petal's own dark tone (1 px, 2 on the lower right), 2-px
stems, pointed leaves with lit edges. 4× density (ADR-0111), drawn natively at 28×28.
Rubric: S4 R4 P4 L4 C4 A- F4. Weakest: the forget-me-nots read as three blue rosettes more than five-petal flowers;
the buttercup's throat is busy. Storybook (A) direction, picked in the 2026-09 art review.`,
  anims: { variants: { frames: FLOWERS } },
});

export default [grass, decal, path, plaza, decalDirt, flower];
